/**
 * Las comunas de una region con precio propio, dentro del modal de la region
 * (ADM-10). Cada fila dice cuanto se aparta de la region, que es lo que
 * justifica que exista, y se edita o se quita ahi mismo. Quitar pide
 * confirmacion en la misma fila: ya se esta dentro de un modal, y abrir otro
 * encima taparia la lista que se esta mirando.
 */
import { useState } from 'react';

import type { TarifaComunaAdmin, TarifaRegionAdmin } from '@bandu/compartido';

import { eliminarTarifaComuna } from '../../servicios/despacho-admin.servicio';
import { pesos } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';

/** Desde cuantas comunas aparece el filtro: con pocas, se leen de un vistazo. */
const FILTRAR_DESDE = 8;

interface ComunasRegionProps {
  region: TarifaRegionAdmin;
  excepciones: TarifaComunaAdmin[];
  alAgregar: () => void;
  alEditar: (excepcion: TarifaComunaAdmin) => void;
  alQuitar: () => void;
}

export function ComunasRegion({
  region,
  excepciones,
  alAgregar,
  alEditar,
  alQuitar,
}: ComunasRegionProps) {
  const [filtro, setFiltro] = useState('');
  const [porQuitar, setPorQuitar] = useState<number | null>(null);
  const [quitando, setQuitando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  const termino = filtro.trim().toLocaleLowerCase('es');
  const visibles = excepciones.filter((excepcion) =>
    excepcion.comuna.toLocaleLowerCase('es').includes(termino),
  );

  async function quitar(id: number): Promise<void> {
    setQuitando(true);
    setFalla(null);

    try {
      await eliminarTarifaComuna(id);
      setPorQuitar(null);
      alQuitar();
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setQuitando(false);
    }
  }

  return (
    <section className="comunas-region" aria-labelledby="comunas-region-titulo">
      <div className="comunas-region-cab">
        <h3 id="comunas-region-titulo">
          Comunas con precio propio
          {excepciones.length > 0 ? (
            <span className="contador-comunas">{excepciones.length}</span>
          ) : null}
        </h3>
        <button type="button" className="btn btn-contorno btn-sm" onClick={alAgregar}>
          <Icono nombre="mas" />
          Agregar comuna
        </button>
      </div>

      {excepciones.length === 0 ? (
        <p className="campo-ayuda">Ninguna: todas cobran la tarifa de la region.</p>
      ) : (
        <>
          {excepciones.length >= FILTRAR_DESDE ? (
            <input
              className="entrada comunas-region-filtro"
              type="search"
              placeholder="Buscar comuna"
              aria-label="Buscar comuna con precio propio"
              value={filtro}
              onChange={(evento) => {
                setFiltro(evento.target.value);
              }}
            />
          ) : null}

          {falla !== null ? (
            <p className="acceso-error" role="alert">
              <Icono nombre="alerta" />
              {falla}
            </p>
          ) : null}

          <ul className="comunas-region-lista">
            {visibles.map((excepcion) => {
              const diferencia = excepcion.costo - region.costo;

              return (
                <li key={excepcion.id}>
                  <div className="comuna-propia-datos">
                    <span className="comuna-propia-nombre">{excepcion.comuna}</span>
                    <span className="comuna-propia-detalle">
                      {diferencia === 0
                        ? 'Igual que la region'
                        : `${pesos(Math.abs(diferencia))} ${diferencia > 0 ? 'mas' : 'menos'} que la region`}
                      {excepcion.plazo !== null ? ` · ${excepcion.plazo}` : ''}
                    </span>
                  </div>
                  <strong className="comuna-propia-costo">{pesos(excepcion.costo)}</strong>

                  {porQuitar === excepcion.id ? (
                    <span className="acciones-fila">
                      <button
                        type="button"
                        className="btn btn-contorno btn-sm"
                        onClick={() => {
                          setPorQuitar(null);
                        }}
                      >
                        No
                      </button>
                      <button
                        type="button"
                        className="btn btn-peligro btn-sm"
                        disabled={quitando}
                        aria-label={`Confirmar: quitar el precio propio de ${excepcion.comuna}`}
                        onClick={() => {
                          void quitar(excepcion.id);
                        }}
                      >
                        {quitando ? 'Quitando…' : 'Si, quitar'}
                      </button>
                    </span>
                  ) : (
                    <span className="acciones-fila">
                      <button
                        type="button"
                        className="btn btn-contorno btn-sm"
                        aria-label={`Editar ${excepcion.comuna}`}
                        onClick={() => {
                          alEditar(excepcion);
                        }}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn btn-contorno btn-sm"
                        aria-label={`Quitar ${excepcion.comuna}`}
                        onClick={() => {
                          setPorQuitar(excepcion.id);
                        }}
                      >
                        Quitar
                      </button>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          {visibles.length === 0 ? (
            <p className="campo-ayuda">Ninguna comuna con precio propio se llama asi.</p>
          ) : null}
        </>
      )}
    </section>
  );
}
